import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/Button/Button";
import {
  clearIncapacityFeedback,
  createIncapacity,
  fetchIncapacities,
  updateIncapacity,
} from "../../redux/incapacitySlice";
import "./IncapacitiesSection.css";
import { safeUrl } from "../../utils/url";

const formatDate = (dateValue) => {
  if (!dateValue) return "-";
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "-";
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const getTotalDays = (startDateValue, endDateValue) => {
  const parse = (v) => {
    const m = String(v || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  };

  const start = parse(startDateValue);
  const end = parse(endDateValue);

  if (!start || !end) return 0;

  const dayMs = 24 * 60 * 60 * 1000;
  const diff = Math.floor((end - start) / dayMs) + 1;
  return diff > 0 ? diff : 0;
};

export default function IncapacitiesSection() {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [imageFile, setImageFile] = useState(null);

  const {
    incapacities,
    statusFetchIncapacities,
    errorFetchIncapacities,
    statusCreateIncapacity,
    errorCreateIncapacity,
    messageCreateIncapacity,
    statusUpdateIncapacity,
    errorUpdateIncapacity,
    messageUpdateIncapacity,
  } = useSelector((state) => state.incapacity);

  useEffect(() => {
    dispatch(fetchIncapacities());
  }, [dispatch]);

  useEffect(() => {
    if (!isModalOpen) {
      dispatch(clearIncapacityFeedback());
    }
  }, [dispatch, isModalOpen]);

  const rows = useMemo(() => {
    return (Array.isArray(incapacities) ? incapacities : []).map((item, index) => {
      const fechaInicio = item?.dateRange?.startDate || item?.fechaInicio || item?.startDate;
      const fechaFin = item?.dateRange?.endDate || item?.fechaFin || item?.endDate;
      const motivo = item?.reason || item?.motivo || "Sin motivo";
      // imageLink llega como enlace firmado temporal (la imagen es privada).
      const image = item?.imageLink || item?.image || "";

      return {
        rowId: item?._id || `incapacidad-${index}`,
        id: index + 1,
        fechaInicio,
        fechaFin,
        diasTotales: getTotalDays(fechaInicio, fechaFin),
        motivo,
        image,
      };
    });
  }, [incapacities]);

  const resetForm = () => {
    setStartDate("");
    setEndDate("");
    setReason("");
    setImageFile(null);
    setEditingItem(null);
  };

  const openCreateModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const openEditModal = (row) => {
    const toDateTimeLocal = (iso) => {
      if (!iso) return "";
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return (
        d.getFullYear() +
        "-" +
        pad(d.getMonth() + 1) +
        "-" +
        pad(d.getDate()) +
        "T" +
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes())
      );
    };

    setEditingItem(row);
    setStartDate(toDateTimeLocal(row.fechaInicio));
    setEndDate(toDateTimeLocal(row.fechaFin));
    setReason(row.motivo === "Sin motivo" ? "" : row.motivo);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!startDate || !endDate || !reason.trim()) {
      return;
    }

    const startIso = new Date(startDate).toISOString();
    const endIso = new Date(endDate).toISOString();

    try {
      if (editingItem) {
        await dispatch(
          updateIncapacity({
            id: editingItem.rowId,
            startDate: startIso,
            endDate: endIso,
            reason: reason.trim(),
            imageFile,
          })
        ).unwrap();
      } else {
        await dispatch(
          createIncapacity({
            startDate: startIso,
            endDate: endIso,
            reason: reason.trim(),
            imageFile,
          })
        ).unwrap();
      }

      closeModal();
    } catch {
      // Feedback is handled from redux state.
    }
  };

  return (
    <section className="profile-submenu-content" role="tabpanel" aria-label="Incapacidades">
      <div className="incapacidades-header">
        <h2>Incapacidades</h2>
        <Button
          type="button"
          variant="outline"
          size="small"
          className="incapacidades-create-btn"
          onClick={openCreateModal}
        >
          Crear
        </Button>
      </div>

      {errorFetchIncapacities && (
        <p className="incapacidades-feedback error">{errorFetchIncapacities}</p>
      )}

      <div className="incapacidades-table-wrap">
        <table className="incapacidades-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Fecha de inicio</th>
              <th>Fecha de fin</th>
              <th>Dias totales</th>
              <th>Motivo de la incapacidad</th>
              <th>Image</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {statusFetchIncapacities === "loading" ? (
              <tr>
                <td colSpan="7" className="incapacidades-empty">
                  Cargando incapacidades...
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan="7" className="incapacidades-empty">
                  No hay incapacidades registradas.
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.rowId}>
                  <td>{row.id}</td>
                  <td>{formatDate(row.fechaInicio)}</td>
                  <td>{formatDate(row.fechaFin)}</td>
                  <td>{row.diasTotales}</td>
                  <td>{row.motivo}</td>
                  <td>
                    {row.image ? (
                      <a href={safeUrl(row.image)} target="_blank" rel="noreferrer">
                        <img
                          className="incapacidad-image"
                          src={row.image}
                          alt={`Soporte incapacidad ${row.id}`}
                        />
                      </a>
                    ) : (
                      <span className="incapacidad-no-image">Sin imagen</span>
                    )}
                  </td>
                  <td>
                    <Button
                      type="button"
                      variant="onlyText"
                      size="small"
                      className="incapacidades-edit-btn"
                      onClick={() => openEditModal(row)}
                    >
                      Editar
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isModalOpen && (
        <div className="incapacidades-modal-backdrop" role="presentation" onClick={closeModal}>
          <div
            className="incapacidades-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Crear incapacidad"
            onClick={(event) => event.stopPropagation()}
          >
            <h3>{editingItem ? "Editar incapacidad" : "Crear incapacidad"}</h3>

            <form className="incapacidades-form" onSubmit={handleSubmit}>
              <label htmlFor="incapacity-start-date">Fecha de inicio</label>
              <input
                id="incapacity-start-date"
                type="datetime-local"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                required
              />

              <label htmlFor="incapacity-end-date">Fecha de fin</label>
              <input
                id="incapacity-end-date"
                type="datetime-local"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                required
              />

              <label htmlFor="incapacity-reason">Motivo</label>
              <textarea
                id="incapacity-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                required
              />

              <label htmlFor="incapacity-image">
                {editingItem ? "Reemplazar soporte (opcional)" : "Soporte (opcional)"}
              </label>
              <input
                id="incapacity-image"
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(event) => setImageFile(event.target.files?.[0] || null)}
              />

              {(errorCreateIncapacity || errorUpdateIncapacity) && (
                <p className="incapacidades-feedback error">
                  {errorCreateIncapacity || errorUpdateIncapacity}
                </p>
              )}
              {(messageCreateIncapacity || messageUpdateIncapacity) && (
                <p className="incapacidades-feedback success">
                  {messageCreateIncapacity || messageUpdateIncapacity}
                </p>
              )}

              <div className="incapacidades-form-actions">
                <Button
                  type="button"
                  variant="secondary"
                  size="small"
                  className="incapacidades-form-btn"
                  onClick={closeModal}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="outline"
                  size="small"
                  className="incapacidades-form-btn"
                  disabled={
                    statusCreateIncapacity === "loading" ||
                    statusUpdateIncapacity === "loading"
                  }
                >
                  {statusCreateIncapacity === "loading" || statusUpdateIncapacity === "loading"
                    ? "Guardando..."
                    : "Guardar"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
